import React, { useState, useEffect } from 'react';
import { api } from '../services/api';
import { Loader2, Trash2, Plus, Tag, AlertCircle } from 'lucide-react';

export const AdminCoupons: React.FC = () => {
    const [coupons, setCoupons] = useState<any[]>([]);
    const [loading, setLoading] = useState(true);
    const [creating, setCreating] = useState(false);
    const [newCode, setNewCode] = useState('');
    const [newPercent, setNewPercent] = useState(20);
    const [error, setError] = useState('');

    const fetchCoupons = async () => {
        try {
            const data = await api.getAdminCoupons();
            setCoupons(data);
        } catch (e) {
            console.error(e);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchCoupons();
    }, []);

    const handleCreate = async (e: React.FormEvent) => {
        e.preventDefault();
        setCreating(true);
        setError('');
        try {
            await api.createAdminCoupon(newCode, newPercent);
            await fetchCoupons();
            setNewCode('');
            setNewPercent(20);
        } catch (err: any) {
            setError(err.message || 'Failed to create coupon');
        } finally {
            setCreating(false);
        }
    };

    const handleDelete = async (id: string) => {
        if (!confirm('Are you sure you want to deactivate this coupon?')) return;
        try {
            await api.deleteAdminCoupon(id);
            await fetchCoupons();
        } catch (e: any) {
            alert(e.message);
        }
    };

    if (loading) return <div className="p-8 flex justify-center"><Loader2 className="animate-spin text-slate-400" /></div>;

    return (
        <div className="max-w-4xl mx-auto p-6">
            <div className="flex justify-between items-center mb-6">
                <div>
                    <h2 className="text-2xl font-bold text-slate-900">Coupon Management</h2>
                    <p className="text-slate-500">Create promo codes for one-time discounts (First Month Only).</p>
                </div>
            </div>

            {/* Create Form */}
            <div className="bg-white p-6 rounded-xl border border-slate-200 shadow-sm mb-8">
                <h3 className="font-semibold text-slate-800 mb-4 flex items-center gap-2">
                    <Plus size={18} /> Create New Coupon
                </h3>
                <form onSubmit={handleCreate} className="flex gap-4 items-end">
                    <div className="flex-1">
                        <label className="block text-xs font-medium text-slate-500 mb-1">PROMO CODE</label>
                        <input
                            type="text"
                            required
                            placeholder="e.g. SUMMER50"
                            value={newCode}
                            onChange={e => setNewCode(e.target.value.toUpperCase())}
                            className="w-full px-3 py-2 border border-slate-200 rounded-lg uppercase"
                        />
                    </div>
                    <div className="w-32">
                        <label className="block text-xs font-medium text-slate-500 mb-1">DISCOUNT (%)</label>
                        <input
                            type="number"
                            required
                            min="1" max="100"
                            value={newPercent}
                            onChange={e => setNewPercent(Number(e.target.value))}
                            className="w-full px-3 py-2 border border-slate-200 rounded-lg"
                        />
                    </div>
                    <button
                        type="submit"
                        disabled={creating || !newCode}
                        className="px-6 py-2 bg-black text-white font-bold rounded-lg hover:bg-slate-800 transition-colors flex items-center gap-2 disabled:opacity-50"
                    >
                        {creating ? <Loader2 size={16} className="animate-spin" /> : 'Create'}
                    </button>
                </form>
                {error && (
                    <div className="flex items-center gap-2 text-red-500 text-sm mt-3 bg-red-50 p-2 rounded">
                        <AlertCircle size={14} /> {error}
                    </div>
                )}
            </div>

            {/* List */}
            <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
                <table className="w-full text-left text-sm">
                    <thead className="bg-slate-50 border-b border-slate-200">
                        <tr>
                            <th className="px-6 py-3 font-semibold text-slate-600">Code</th>
                            <th className="px-6 py-3 font-semibold text-slate-600">Discount</th>
                            <th className="px-6 py-3 font-semibold text-slate-600">Redeemed</th>
                            <th className="px-6 py-3 font-semibold text-slate-600">Status</th>
                            <th className="px-6 py-3 text-right">Actions</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                        {coupons.map(coupon => (
                            <tr key={coupon.id} className="hover:bg-slate-50 transition-colors">
                                <td className="px-6 py-4 font-medium text-slate-900 flex items-center gap-2">
                                    <Tag size={14} className="text-slate-400" />
                                    {coupon.code}
                                </td>
                                <td className="px-6 py-4">
                                    <span className="bg-green-100 text-green-700 px-2 py-1 rounded text-xs font-bold">
                                        {coupon.percentOff}% OFF
                                    </span>
                                </td>
                                <td className="px-6 py-4 text-slate-500">{coupon.timesRedeemed} uses</td>
                                <td className="px-6 py-4">
                                    {coupon.active ?
                                        <span className="text-green-600 font-medium text-xs">Active</span> :
                                        <span className="text-slate-400 font-medium text-xs">Inactive</span>
                                    }
                                </td>
                                <td className="px-6 py-4 text-right">
                                    {coupon.active && (
                                        <button
                                            onClick={() => handleDelete(coupon.id)}
                                            className="text-red-400 hover:text-red-600 transition-colors"
                                            title="Deactivate"
                                        >
                                            <Trash2 size={16} />
                                        </button>
                                    )}
                                </td>
                            </tr>
                        ))}
                        {coupons.length === 0 && (
                            <tr>
                                <td colSpan={5} className="px-6 py-8 text-center text-slate-400">
                                    No active coupons found. Create one above.
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>
        </div>
    );
};
